// Qué le ofrece el Home a cada CAPA (estudiante / interno / enfermero / TENS).
// Dominio puro: el Home solo pinta lo que sale de aquí. Nació el 2026-09-14
// porque las tres superficies que deciden "qué se le muestra a quién" (menú
// hamburguesa, buscador y Home) se contradecían: el menú le escondía el PAE a
// la capa de ejercicio (dictado del fundador 2026-09-10) mientras el Home le
// ofrecía "Arma un PAE" como actividad del día, y el TENS, que tiene su PROPIO
// MiPAE en turno (lib/tens.js), abría un Home que le destacaba el PAE Rápido
// y jamás le nombraba su turno.
//
// Regla desde el 2026-10-16 (interfaz por experiencia): el protagonista del
// inicio es EL MOMENTO de cada capa, arriba; aquí abajo van cuatro atajos a
// lo que esa capa usa de verdad, sin destacado. Las versiones anteriores
// (un "Explora" con una tarjeta destacada por capa) quedaron en la historia.
import { esCapaEjercicio, esElegibleTurno, veEnTurnoEstudiante } from './perfil';
import { esTens } from './tens';

// Catálogo cerrado de atajos. `module` es el token de color de HomeCard (el
// del módulo que ABRE: lo que vive en la Enciclopedia lleva el suyo),
// `accion` el id que Home traduce a su callback. Desde el 2026-10-16 van en
// una grilla de dos columnas, así que la descripción es corta: dice para qué
// sirve en una línea, no qué contiene. Copy sin guiones largos.
const TARJETAS = Object.freeze({
  'pae-rapido': {
    id: 'pae-rapido', module: 'pae-rapido', accion: 'rapido', label: 'PAE Rápido',
    description: 'Un caso, paso a paso.',
  },
  // Modelos de enfermería (P3, decisión del fundador 2026-10-04): reemplazó
  // al PAE Detallado en el inicio de formación. El Detallado está pensado
  // para el paciente crítico y sigue en el menú y el buscador.
  modelos: {
    id: 'modelos', module: 'enciclopedia', accion: 'modelos', label: 'Modelos de enfermería',
    description: 'Doce modelos para valorar y justificar tu PAE.',
  },
  estudio: {
    id: 'estudio', module: 'enciclopedia', accion: 'estudio', label: 'Modo estudio',
    description: 'Preguntas para repasar.',
  },
  escalas: {
    id: 'escalas', module: 'escalas', accion: 'escalas', label: 'Escalas clínicas',
    description: 'Glasgow, Braden, EVA y más.',
  },
  medicamentos: {
    id: 'medicamentos', module: 'medicamentos', accion: 'medicamentos', label: 'Medicamentos',
    description: 'Antes de administrar.',
  },
  // Revisa vuelve al inicio de MiPAE Enfermero (decisión del fundador del
  // 2026-10-16, opción B: solo ahí, no en Estudiante, la interna ni el TENS).
  // Toma el lugar de Calculadoras, el atajo menos usado (en 30 días la
  // abrieron 2 de 64 enfermeros activos, contra 12 que abrieron Revisa).
  // Calculadoras sigue en la Enciclopedia y en el buscador.
  revisa: {
    id: 'revisa', module: 'revisa', accion: 'revisa', label: 'Revisa',
    description: 'Dudas del cuidado, con sus fuentes.',
  },
  laboratorio: {
    id: 'laboratorio', module: 'enciclopedia', accion: 'laboratorio', label: 'Valores de laboratorio',
    description: 'Normal, alto o bajo.',
  },
  // Los cuidados del paciente (plan del mundo TENS, PR8): la sección
  // Procedimientos de la Enciclopedia, que abre con ellos para el TENS.
  cuidados: {
    id: 'cuidados', module: 'enciclopedia', accion: 'procedimientos', label: 'Cuidados del paciente',
    description: 'Con su porqué y de qué avisar.',
  },
  // Aislamiento y EPP de la Enciclopedia, con su nombre y su ícono de allá.
  aislamiento: {
    id: 'aislamiento', module: 'enciclopedia', accion: 'aislamiento', label: 'Aislamiento y EPP',
    description: 'Precauciones y secuencia de EPP.',
  },
});

// Qué atajos ve cada experiencia (propuesta de la interfaz por experiencia,
// aprobada por el fundador el 2026-10-16, docs/plan-interfaz-por-experiencia.md §2):
// - Estudiante: lo de su formación, PAE Rápido, Modelos, Escalas y Modo estudio.
// - Interna o interno en curso: el PAE sigue (no se esconde por estar en
//   internado) y suma lo que se consulta en el turno.
// - Enfermero: "Consulta rápida", solo consulta del turno, con Revisa. El PAE
//   salió de MiPAE Enfermero entero (decisión 3 del 16-oct): vive en MiPAE
//   Estudiante.
// - TENS: Cuidados, Escalas, Medicamentos y Aislamiento. MiPAE en turno ya no
//   es atajo: es su card protagonista (MomentoTens, paso 2, maqueta
//   "Panorama" del 2026-10-16), y repetirlo abajo diría lo mismo dos veces.
// Ya no hay atajo destacado: la card protagonista del inicio es EL MOMENTO
// (una por pantalla, contrato de Card).
const ATAJOS_POR_CAPA = Object.freeze({
  estudiante: Object.freeze(['pae-rapido', 'modelos', 'escalas', 'estudio']),
  interna: Object.freeze(['pae-rapido', 'escalas', 'medicamentos', 'modelos']),
  enfermero: Object.freeze(['escalas', 'medicamentos', 'revisa', 'laboratorio']),
  tens: Object.freeze(['cuidados', 'escalas', 'medicamentos', 'aislamiento']),
});

function capaDeAtajos(profile) {
  if (esTens(profile)) return 'tens';
  if (esCapaEjercicio(profile)) return 'enfermero';
  if (profile?.rol === 'estudiante' && esElegibleTurno(profile)) return 'interna';
  return 'estudiante';
}

/**
 * Atajos del inicio para la capa del perfil, en orden.
 * @param {object|null} profile
 * @returns {Array<{id,module,accion,label,description}>}
 */
export function tarjetasExplora(profile) {
  return ATAJOS_POR_CAPA[capaDeAtajos(profile)].map((k) => ({ ...TARJETAS[k] }));
}

/** El título sobre los atajos: "Consulta rápida" en MiPAE Enfermero, "Tus atajos" en el resto. */
export function tituloAtajos(profile) {
  return capaDeAtajos(profile) === 'enfermero' ? 'Consulta rápida' : 'Tus atajos';
}

// Actividades que marcan el día en la racha ("¿qué haces hoy?"). Catálogo
// cerrado; el Home les pone icono y callback.
// "Reflexiona" (la Bitácora) salió del catálogo el 4-oct-2026 (A23): la regla
// del 2026-09-24 es que el autocuidado no se empuja, jamás como tarea, y una
// actividad de la racha es justo eso. Reflexionar sigue sumando a la racha
// cuando la persona va a la Bitácora por su cuenta; solo no se le pide.
export const ACTIVIDADES_RACHA = Object.freeze({
  reto:     { id: 'reto',     label: 'El reto del día' },
  turno:    { id: 'turno',    label: 'Documenta tu turno' },
  pae:      { id: 'pae',      label: 'Arma un PAE' },
});

/**
 * Qué actividades se le ofrecen a la capa para marcar el día.
 * - El reto es universal (lo puede hacer cualquiera, incluso en día libre).
 * - El turno va a quien tiene superficie de turno: elegibles Y el TENS (su
 *   MiPAE en turno es la capa TENS; antes quedaba fuera por esElegibleTurno).
 * - Armar un PAE es de FORMACIÓN: a la capa de ejercicio el menú ya no le
 *   muestra el PAE, así que el Home tampoco se lo pide como tarea del día. Y
 *   tampoco al TENS (D15, 4-oct-2026): su registro son cuidados delegados y
 *   observación, el PAE no es su herramienta del día.
 * @param {object|null} profile
 * @returns {Array<{id,label}>}
 */
export function actividadesRacha(profile) {
  const ids = ['reto'];
  if (esElegibleTurno(profile) || esTens(profile)) ids.push('turno');
  if (!esCapaEjercicio(profile) && !esTens(profile)) ids.push('pae');
  return ids.map((k) => ACTIVIDADES_RACHA[k]);
}

// La card protagonista del estudiante en el inicio (I4 del plan de la
// interfaz de los tres mundos, decisión del fundador 2026-10-04): el
// enfermero abre con su turno (EL MOMENTO), y el estudiante, que no tiene
// turno, abría sin un "qué hago ahora". Va SOLO a quien MiPAE en turno le
// abre la presentación (estudiante sin internado en curso): la interna en
// curso ya tiene su EL MOMENTO, y dos protagonistas por pantalla rompen el
// contrato de Card.
export function veMomentoEstudiante(profile) {
  return veEnTurnoEstudiante(profile);
}

/**
 * Qué card protagonista ve el estudiante.
 * - Un PAE sin terminar (el borrador local de App, Rápido o Detallado) → seguirlo.
 * - Si no, y ya hizo el reto de hoy → "Arma un PAE" (caso 3 del plan, §2.2).
 * - Si no, el Reto del día.
 * `retoHecho` lo resuelve lib/retoHecho.js (sigue a la cuenta, decisión del
 * fundador 2026-10-04). El borrador gana: seguir lo empezado va primero.
 * @param {{borrador?: object|null, retoHecho?: boolean}} args
 * @returns {{tipo:'borrador', modo:'rapido'|'detallado', timestamp:number}|{tipo:'pae'}|{tipo:'reto'}}
 */
export function momentoEstudiante({ borrador, retoHecho = false } = {}) {
  if (borrador && typeof borrador === 'object') {
    return {
      tipo: 'borrador',
      modo: borrador.mode === 'detallado' ? 'detallado' : 'rapido',
      timestamp: Number(borrador.timestamp) || 0,
    };
  }
  if (retoHecho === true) return { tipo: 'pae' };
  return { tipo: 'reto' };
}

// Los cuatro "Prepara" del menú ("MiPAE te acompaña"). Decisión del fundador
// 2026-10-04 (P2): al estudiante se le muestra SOLO el que calza con su etapa,
// porque cuatro guías seguidas para un mismo momento confundían. Los cuatro
// siguen en el buscador para la formación. Las cuatro son guías de la
// formación de enfermería (práctica, internado, examen), así que ni la capa
// de ejercicio ni el TENS las ven (el TENS las recibía por no ser estudiante,
// hallazgo del 2026-10-15). Un perfil sin rol los ve todos, como siempre.
//   práctica de pregrado o sin etapa → Prepara tu práctica
//   internado por empezar           → Prepara tu internado
//   internado en curso              → Prepara tu turno
//   internado terminado             → Prepara tu examen
export const PREPARA_IDS = Object.freeze(['prepara-practica', 'prepara-internado', 'prepara-turno', 'prepara-examen']);
const PREPARA_POR_ETAPA = Object.freeze({
  pre_internado: 'prepara-internado',
  internado_en_curso: 'prepara-turno',
  post_internado: 'prepara-examen',
});
/**
 * @param {object|null} profile
 * @returns {string[]} ids de PREPARA_IDS, en el orden del menú
 */
export function preparaDelMenu(profile) {
  if (esCapaEjercicio(profile) || esTens(profile)) return [];
  if (profile?.rol !== 'estudiante') return [...PREPARA_IDS];
  return [PREPARA_POR_ETAPA[profile?.etapa_internado] || 'prepara-practica'];
}
